import { isNotNullish, isUndefined } from '@/utils/is'
import type { IInput } from './input/types'
import type { TFormModel } from './types/formModel'
import type { IForm } from './types/form'
import type { FormFieldRef, TFormFieldDict } from './types/field'
import { isRef, type Ref } from 'vue'
import type { StringIndexable } from '@/types/object'

export type FormError = {
  message: string;
  hidden?: boolean;
}

export interface FormErrorMap {
  [key: string]: FormError[] | FormErrorMap
}

export type FormValidationContext<T extends TFormModel = TFormModel, ContextType extends (StringIndexable | undefined) = StringIndexable> = {
  state: T;
  context?: Ref<ContextType>;
}

export type FormValidatorResult = FormError[] | FormError | undefined | null

export type FormValidatorFunction<T extends TFormModel = TFormModel, ContextType extends (StringIndexable | undefined) = StringIndexable> = (
  input: IInput,
  context: FormValidationContext<T, ContextType>
) => FormValidatorResult

export type FormValidator<T extends TFormModel = TFormModel, ContextType extends (StringIndexable | undefined) = StringIndexable> = FormValidatorFunction<T, ContextType>

export const validateField = <T extends TFormModel = TFormModel, ContextType extends (StringIndexable | undefined) = StringIndexable>(
  input: IInput,
  context: FormValidationContext<T, ContextType>
): FormError[] => {
  if (input.hidden === true) return [];
  const validators = input.validators || input.schema?.$validators
  if (!validators) return []
  const results = validators
    .map((validator) => validator(input, context as FormValidationContext<any, any>))
    .flat()
    .filter(isNotNullish)
  if (results.length <= 0) return []
  return results
}

const isField = (x: any): x is FormFieldRef => {
  if (!x) return false
  if (!isRef(x)) return false
  if (typeof x.value !== 'object') return false
  if (!x.value) return false
  const keys = Object.keys(x.value)
  if (!keys.includes('value')) return false
  if (!keys.includes('type')) return false
  if (!keys.includes('validators')) return false
  return true
}
const validateFormOrField_ = <T extends TFormModel = TFormModel, ContextType extends (StringIndexable | undefined) = StringIndexable>(
  dict: TFormFieldDict<T> | FormFieldRef,
  schema: IForm<T, ContextType>,
  state: T,
  wrapper: (field: FormFieldRef, errors: FormError[]) => FormError[],
  userContext?: Ref<ContextType>
): FormErrorMap => {
  const context: FormValidationContext<T, ContextType> = { state, context: userContext }

  if (isUndefined(dict) || !dict) return {}
  if (isField(dict))
    return { [dict.value.name || '?']: wrapper(dict, validateField<T, ContextType>(dict.value, context)) }

  return Object.assign(
    {},
    ...Object.entries(dict).map(([key, field]) => {
      if (isField(field)) return { [key]: wrapper(field, validateField<T, ContextType>(field.value, context)) }
      return {
        [key]: validateFormOrField<T, ContextType>(
          field as any,
          (schema as any)[key as keyof typeof schema] as IForm<T, ContextType>,
          state,
          wrapper,
          userContext
        )
      }
    })
  )
}

export const validateFormOrField = <T extends TFormModel = TFormModel, ContextType extends (StringIndexable | undefined) = StringIndexable>(
  dict: TFormFieldDict<T> | FormFieldRef,
  schema: IForm<T, ContextType>,
  state: T,
  errorCallback?: (field: FormFieldRef, errors: FormError[]) => void,
  userContext?: Ref<ContextType>,
): [FormErrorMap, number] => {
  const _state: { count: number } = { count: 0 }

  return [
    validateFormOrField_(dict, schema, state, (field: FormFieldRef, errors: FormError[]) => {
      if (field.value.hidden) return [];
      if (errors && errors.length > 0) {
        _state.count += 1
      }
      if (errorCallback) {
        errorCallback(field, errors || [])
      }
      return errors
    }, userContext),
    _state.count
  ]
}

export const countFormErrors = (errors: FormErrorMap) => {
  let n: number = 0

  for (const [_key, value] of Object.entries(errors)) {
    if (Array.isArray(value)) {
      n += value.length
    } else if (typeof value === 'object') {
      n += countFormErrors(value)
    }
  }

  return n
}
